import type React from "react";
import { type ReactNode, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@krea/shared/design-system/Button";
import { cn } from "@krea/shared/utils";

import type { ISEBankIDProps } from "../common-types";
import { BankIDAuthenticationState } from "../components/BankIDAuthenticationState";
import { BANKID_TEST_IDS } from "../test-constants";
import {
  buildBankIDAutostartUrl,
  isMobileUserAgent,
} from "./buildAutostartUrl";
import { AlternativeAuthOptions } from "./components/AlternativeAuthOptions";
import { AutoStartAuthFlow } from "./components/AutoStartAuthFlow";
import { BankIDLogo } from "./components/BankIDLogo";
import { QRCodeAuthFlow } from "./components/QRCodeAuthFlow";
import { SSNAuthFlow } from "./components/SSNAuthFlow";
import { SEBankIDAuthType, SEBankIDHintCode } from "./types";
import { useSEBankID } from "./useSEBankID";

export const SEBankIDFlow: React.FC<ISEBankIDProps> = ({
  authenticationContext,
  identificationContext,
  onSuccess,
  onError,
  onCancel,
  extraData,
  autoInit,

  errorComponent,
}) => {
  const [testFlowSSN, setTestFlowSSN] = useState("");
  const [isTestFlowSubmitted, setIsTestFlowSubmitted] = useState(false);
  const { t } = useTranslation();

  const {
    authType,
    state,
    qrCode,
    autoStartToken,
    changeAuthType,
    start,
    cancel,
  } = useSEBankID({
    authenticationContext,
    identificationContext,
    onSuccess,
    onError,
    onCancel,
    testSSN: testFlowSSN,
    extraData,
  });

  // Open BankID app on this device
  const openBankIDOnThisDevice = useCallback((autoStartToken: string) => {
    if (!autoStartToken) return;

    // The URL carries no return address, so this hands off to the BankID app
    // without ever navigating this page. Keeping the document alive is what lets
    // the collect poll still be running when the user switches back. See
    // NO_REDIRECT in buildAutostartUrl.
    window.location.href = buildBankIDAutostartUrl({
      autoStartToken,
      userAgent: navigator.userAgent,
    });
  }, []);

  // Open BankID app when auto-start token is available
  useEffect(() => {
    if (
      authType === SEBankIDAuthType.AUTO_START &&
      autoStartToken &&
      state.status === "pending"
    ) {
      openBankIDOnThisDevice(autoStartToken);
    }
  }, [authType, autoStartToken, state.status, openBankIDOnThisDevice]);

  const handleChangeAuthType = useCallback(
    (newType: SEBankIDAuthType) => {
      setIsTestFlowSubmitted(false);
      changeAuthType(newType);
    },
    [changeAuthType],
  );

  const handleTestFlowSubmit = () => {
    setIsTestFlowSubmitted(true);
    start(SEBankIDAuthType.SSN);
  };
  const defaultAuthType = isMobileUserAgent(navigator.userAgent)
    ? SEBankIDAuthType.AUTO_START
    : SEBankIDAuthType.QR_CODE;

  const handleCancel = useCallback(() => {
    cancel();
    setIsTestFlowSubmitted(false);

    if (onCancel) {
      onCancel();
    }
  }, [cancel, onCancel]);

  const handleInitialAuth = () => {
    start(defaultAuthType);
  };

  // Starts from a timer the effect's own cleanup can cancel, and never on a
  // re-render. React throws away the first mount pass (StrictMode in dev), and a
  // mutation fired inside that pass loses its useMutation observer: authenticate
  // succeeds and its onSuccess still runs, but `data.orderRef` never reaches the
  // component — so `collect` stays disabled and nothing polls. Deferring means
  // the order is only ever created by a settled, subscribed observer.
  // Mount-only also keeps a cancel — which returns the flow to "idle" — showing
  // the start button instead of immediately opening another order.
  // biome-ignore lint/correctness/useExhaustiveDependencies: mount-only on purpose; start and defaultAuthType are stable from the first render
  useEffect(() => {
    if (!autoInit) return;

    const timer = setTimeout(() => start(defaultAuthType), 0);

    return () => clearTimeout(timer);
  }, [autoInit]);

  const getPendingStateTitle = () => {
    if (
      authType === SEBankIDAuthType.QR_CODE &&
      state.status === "pending" &&
      state.hintCode === SEBankIDHintCode.USER_SIGN
    ) {
      return t("bankID.SE.writeSecurityCode");
    }

    if (authType === SEBankIDAuthType.AUTO_START) {
      return t("bankID.SE.autostartTitle");
    }

    return null;
  };

  // Render: Success state
  if (state.status === "success") {
    return null;
  }

  // Render: Error state
  if (state.status === "error") {
    let renderErrorComponent: ReactNode | null = null;

    if (errorComponent) {
      renderErrorComponent =
        typeof errorComponent === "function"
          ? errorComponent({
              error: state.error,
              hintCode: state.hintCode,
            })
          : errorComponent;
    }

    return (
      <div className="flex w-full flex-col items-center">
        {renderErrorComponent ? (
          renderErrorComponent
        ) : (
          <BankIDAuthenticationState error>
            {state.error.message}
          </BankIDAuthenticationState>
        )}
        <Button variant="ghost" className="mt-6" onClick={handleCancel}>
          {t("bankID.common.back")}
        </Button>
      </div>
    );
  }

  // Render: Cancelled state
  if (state.status === "cancelled") {
    return (
      <div className="flex w-full flex-col items-center">
        <h2 className="text-center">{t("bankID.common.canceledTitle")}</h2>
        <BankIDAuthenticationState>
          {t("bankID.common.loginCancelled")}
        </BankIDAuthenticationState>
        <Button variant="ghost" className="mt-6" onClick={handleCancel}>
          {t("bankID.common.back")}
        </Button>
      </div>
    );
  }

  // Render: Pending/Authenticating state
  if (state.status === "pending" || state.status === "authenticating") {
    const isUserSign =
      state.status === "pending" &&
      state.hintCode === SEBankIDHintCode.USER_SIGN;

    const authTypeComponents = {
      [SEBankIDAuthType.QR_CODE]: (
        <QRCodeAuthFlow qrCode={qrCode} isUserSign={isUserSign} />
      ),
      [SEBankIDAuthType.AUTO_START]: <AutoStartAuthFlow />,
      [SEBankIDAuthType.SSN]: (
        <SSNAuthFlow
          ssn={testFlowSSN}
          onSSNChange={setTestFlowSSN}
          onSubmit={handleTestFlowSubmit}
          isSubmitting={isTestFlowSubmitted}
        />
      ),
    };

    return (
      <div className="flex w-full flex-col items-center">
        <h2 className="text-center">{getPendingStateTitle()}</h2>
        {authType ? authTypeComponents[authType] : null}

        {!isUserSign && (
          <AlternativeAuthOptions
            currentAuthType={authType}
            onChangeAuthType={handleChangeAuthType}
          />
        )}

        <Button variant="ghost" className="mt-6" onClick={handleCancel}>
          {t("bankID.SE.abortLogin")}
        </Button>
      </div>
    );
  }

  if (autoInit && state.status === "idle") {
    return null; // Don't render anything while autoInit is starting the flow
  }

  // Render: Initial/Idle state
  return (
    <div className="flex w-full flex-col items-center">
      <Button
        size="lg"
        className={cn("text-center w-full max-w-[400px] text-lg")}
        onClick={handleInitialAuth}
        data-test-id={BANKID_TEST_IDS.mobileBankIdButton}
      >
        <BankIDLogo color="white" className="size-10" />
        {t("bankID.SE.startBankId")}
      </Button>
    </div>
  );
};
